import React, { useState, useEffect, useCallback } from 'react';
import { sortBy } from 'lodash';
import {
  GoogleMap,
  Marker,
  Polygon,
  useJsApiLoader,
} from '@react-google-maps/api';
import { Box, Card, Typography } from '@mui/material';
import { Location } from '@procore/core-icons/dist';
import ReactDOMServer from 'react-dom/server';
import getConfig from 'next/config';
import { useI18nContext } from '@procore/core-react';
import { DOMParser } from '@xmldom/xmldom';
import window from '../../globalWrappers/window';
import { Business } from '../../types/business';
import { DEFAULT_COUNTRY, CANADA } from '../../data/location/countries';
import { getStateList } from '../../data/location/states';
import { useStyles } from './AreaOfCoverage.styles';
import { LatLng } from '../../types/maps';
import {
  fetchStatesShapes,
  fetchLocationShapes,
} from '../../data/location/shapes';

const { publicRuntimeConfig } = getConfig();

interface AreaOfCoverageProps {
  business: Business;
  location: LatLng;
}

const statesObject = {};
[...getStateList(DEFAULT_COUNTRY), ...getStateList(CANADA)].forEach((state) => {
  statesObject[state.abbreviation] = state.name;
});

export const sortCoverageAreas = (coverageAreas) => {
  const stateWide = sortBy(
    coverageAreas
      .filter((ca) => !ca.locality)
      .map((ca) => statesObject[ca.admin1?.toUpperCase()])
  );
  const cityWide = sortBy(
    coverageAreas
      .filter((ca) => ca.locality)
      .map((ca) => `${ca.locality}, ${ca.admin1?.toUpperCase()}`)
  );
  return [...cityWide, ...stateWide].filter((ca) => ca);
};

const AreaOfCoverage = ({ business, location }: AreaOfCoverageProps) => {
  const I18n = useI18nContext();
  const classes = useStyles();
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: publicRuntimeConfig.mapsKey,
  });
  const mapOptions: google.maps.MapOptions = {
    fullscreenControl: false,
    mapTypeControl: false,
    streetViewControl: false,
  };
  const [polygons, setPolygons] = useState([]);
  const [sortedCoverageAreas, setSortedCoverageAreas] = useState([]);
  const [showMoreOrLessText, setShowMoreOrLessText] = useState<
    'more' | 'less' | null
  >(null);

  const setCenterToLocation = useCallback(
    (map) => {
      map.setCenter(location);
      map.setZoom(9);
    },
    [location]
  );

  const setShapes = (map, polygonShapes, loadingPolygons) => {
    const bounds = new google.maps.LatLngBounds();
    const parsedPolygonShapes = [];
    polygonShapes.forEach((polygon) => {
      if (polygon?.length) {
        parsedPolygonShapes.push(polygon);
        polygon.forEach((latlng: LatLng) => {
          bounds.extend(latlng);
        });
      }
    });
    if (parsedPolygonShapes.length) {
      setPolygons(parsedPolygonShapes);
      map.fitBounds(bounds);
    } else if (!loadingPolygons) {
      setCenterToLocation(map);
    } else {
      map.setZoom(9);
    }
  };

  const onLoad = useCallback(function callback(map) {
    setShapes(map, [], true);
    const items = (business?.coverageAreas ?? [])
      .filter((ca) => ca?.countryCode && ca?.admin1)
      .map((ca) => ({
        address: {
          countryCode: ca.countryCode,
          stateCode: ca.admin1,
          city: ca.locality,
        },
      }));
    if (items?.length) {
      const citiesItems = [];
      const statesItems = [];
      items?.forEach((item) => {
        if (item.address.city) {
          citiesItems.push(item);
        } else {
          statesItems.push(item);
        }
      });
      Promise.all([
        fetchStatesShapes(statesItems),
        fetchLocationShapes(citiesItems),
      ]).then(([statesResponse, citiesResponse]) => {
        const polygons = [];
        Object.values(statesResponse).forEach((value) => {
          polygons.push(...value);
        });
        Object.values(citiesResponse).forEach((value) => {
          polygons.push(...value);
        });
        setShapes(map, polygons, false);
      });
    } else {
      setShapes(map, [], false);
    }
  }, []);

  useEffect(() => {
    if (business.coverageAreas?.length > 5) {
      setShowMoreOrLessText('more');
    }

    setSortedCoverageAreas(sortCoverageAreas(business.coverageAreas || []));
  }, [business.coverageAreas]);

  // https://stackoverflow.com/a/68889169
  // https://stackoverflow.com/a/17310434
  const iconString = ReactDOMServer.renderToString(<Location />);
  const parser = new DOMParser();
  const svgDoc = parser.parseFromString(iconString, 'image/svg+xml');
  const iconPath = svgDoc.documentElement
    .getElementsByTagName('path')?.[0]
    ?.getAttribute('d');
  const headerTitle = I18n.t('components.area_of_coverage.service_areas_for', {
    name: business.display.name,
  });

  return isLoaded ? (
    <Box
      component="map"
      display="flex"
      flexDirection="column"
      style={{ paddingTop: '24px' }}
    >
      <Card style={{ boxShadow: 'none', paddingBottom: '48px' }}>
        <Box p={0}>
          <Typography variant="h2" className={classes.coverageAreasHeader}>
            {business.coverageAreas?.length > 0 && polygons.length > 0
              ? headerTitle
              : I18n.t('components.area_of_coverage.business_map_for', {
                  name: business.display.name,
                })}
          </Typography>
          <Box className={classes.coverageAreasMargins}>
            <Typography
              className={classes.coverageAreasText}
              data-test-id="coverage-areas-text"
            >
              {showMoreOrLessText === 'more'
                ? sortedCoverageAreas.slice(0, 5).join(' • ')
                : sortedCoverageAreas.join(' • ')}
            </Typography>
            {showMoreOrLessText !== null && (
              <Typography
                className={classes.showMoreOrLessText}
                onClick={() =>
                  setShowMoreOrLessText(
                    showMoreOrLessText === 'more' ? 'less' : 'more'
                  )
                }
                data-test-id="show-more-or-less-text"
              >
                {showMoreOrLessText === 'more'
                  ? I18n.t('components.area_of_coverage.show_more')
                  : I18n.t('components.area_of_coverage.show_less')}
              </Typography>
            )}
          </Box>
          <GoogleMap
            mapContainerStyle={{
              height: '400px',
              borderRadius: '4px',
            }}
            onLoad={onLoad}
            options={mapOptions}
          >
            {location && (
              <Marker
                opacity={0.5}
                position={location}
                icon={{
                  path: iconPath,
                  strokeColor: 'black',
                  fillColor: '#FF5100',
                  fillOpacity: 0.7,
                  anchor: new window.google.maps.Point(10, 18),
                }}
              />
            )}
            {polygons &&
              polygons.map((polygon: LatLng[]) => (
                <Polygon
                  key={`${polygon[0].lat}-${polygon[0].lng}`}
                  path={polygon}
                  options={{
                    fillColor: '#0033A1',
                    fillOpacity: 0.3,
                    strokeColor: '#3397FF',
                    strokeOpacity: 1,
                    strokeWeight: 3,
                  }}
                />
              ))}
          </GoogleMap>
        </Box>
      </Card>
    </Box>
  ) : (
    <></>
  );
};

export default AreaOfCoverage;
